// /src/utils/upload/Upload.tsx
import React, {useRef} from 'react';
import '../../styles/ui-buttons.scss';

interface UploadProps {
  onUpload: (file: File, metadata: Record<string, any>) => void;
  metadata?: Record<string, any>;
  apiParams?: Record<string, any>;
  disabled?: boolean;
  /** Show the chosen picture under the button (default). The Account section shows its own picture instead. */
  showPreview?: boolean;
}

const DEFAULT_METADATA = {
  title: 'Profile Picture',
  description: 'User profile image',
  version: '1.0',
  doctype: 'image',
  tags: ['user', 'profile'],
};

export const Upload: React.FC<UploadProps> = ({
  onUpload,
  metadata = DEFAULT_METADATA,
  apiParams = {},
  disabled = false,
  showPreview = true,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [previewUrl, setPreviewUrl] = React.useState<string | null>(null);
  const previewUrlRef = React.useRef<string | null>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Clean up previous preview URL
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    }
    const file = e.target.files?.[0];
    if (!file) return;

    // File size check (≤ 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('File size must be 5MB or less.');
      return;
    }

    // File type check (.jpg, .jpeg, .png, .gif)
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif'];
    const fileName = file.name.toLowerCase();
    const isJpegExt = fileName.endsWith('.jpg') || fileName.endsWith('.jpeg');
    const isPngExt = fileName.endsWith('.png');
    const isGifExt = fileName.endsWith('.gif');
    const isKnownImageType = allowedTypes.includes(file.type);
    // Accept if MIME type matches, or extension matches for common image types
    if (!isKnownImageType && !(isJpegExt || isPngExt || isGifExt)) {
      alert('Only JPG, JPEG, PNG, or GIF files are allowed.');
      return;
    }

    // Image dimension check (square, max 500x500)
    const img = new window.Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      if (img.width !== img.height) {
        alert('Image must be square.');
        URL.revokeObjectURL(url);
        return;
      }
      if (img.width > 500 || img.height > 500) {
        alert('Image must be 500x500 pixels or smaller.');
        URL.revokeObjectURL(url);
        return;
      }
      setPreviewUrl(url);
      previewUrlRef.current = url;
      onUpload(file, { ...DEFAULT_METADATA, ...metadata, ...apiParams });
    };
    img.onerror = () => {
      alert('Invalid image file.');
      URL.revokeObjectURL(url);
    };
    img.src = url;
  };

  React.useEffect(() => {
    // Cleanup on unmount
    return () => {
      if (previewUrlRef.current) {
        URL.revokeObjectURL(previewUrlRef.current);
        previewUrlRef.current = null;
      }
    };
  }, []);

  return (
    <div>
      <label htmlFor="file-upload" className="ui-button" style={disabled ? { opacity: 0.5, cursor: 'default' } : undefined}>
        Upload Your Pic
        <input
          id="file-upload"
          type="file"
          accept=".jpg,.jpeg,.png,.gif"
          ref={fileInputRef}
          onChange={handleFileChange}
          style={{ display: 'none' }}
          disabled={!!disabled}
        />
      </label>
      {showPreview && previewUrl && (
        <img
          src={previewUrl}
          alt="Preview"
          style={{
            width: 42,
            height: 42,
            borderRadius: '50%',
            objectFit: 'cover',
            display: 'block',
            marginTop: 8,
          }}
        />
      )}
    </div>
  );
};

export default Upload;